import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageSquare, ArrowLeft, Send, Loader2, ExternalLink } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";

const TARGET_LINK = {
  listing: (id) => `/marketplace/${id}`,
  event: (id) => `/events/${id}`,
  job: (id) => `/jobs/${id}`,
  need: (id) => `/needs/${id}`,
  business: (slug) => `/business/${slug}`,
};

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString();
}

function Avatar({ user }) {
  const initial = (user?.name || "?").slice(0, 1).toUpperCase();
  return (
    <div className="w-11 h-11 rounded-full bg-[#1E3A2F] text-[#F7F4EB] font-display grid place-items-center shrink-0">
      {initial}
    </div>
  );
}

function ConversationRow({ conv, active, onClick }) {
  const other = conv.other;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-[#F7F4EB] transition ${active ? "bg-[#F7F4EB]" : ""}`}
      data-testid={`inbox-conv-${conv.id}`}
    >
      <Avatar user={other} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className="font-medium text-[#1E3A2F] truncate flex items-center gap-1">
            {other?.name || "Member"}
            {other?.verified && <VerifiedBadge tier={other?.verification_tier} size="sm" />}
          </div>
          <div className="text-xs text-[#5C5C5C] shrink-0">{timeAgo(conv.last_message_at || conv.created_at)}</div>
        </div>
        <div className="text-sm text-[#5C5C5C] truncate">{conv.last_message_preview || <span className="italic">No messages yet</span>}</div>
        {conv.target_title && (
          <div className="text-[11px] text-[#C05A3D] mt-0.5 truncate">re: {conv.target_title}</div>
        )}
      </div>
      {conv.unread > 0 && (
        <span data-testid={`inbox-unread-${conv.id}`} className="ml-1 min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#C05A3D] text-white text-[11px] font-semibold grid place-items-center shrink-0">{conv.unread}</span>
      )}
    </button>
  );
}

function MessageBubble({ msg, mine }) {
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`inbox-msg-${msg.id}`}>
      <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap break-words ${mine ? "bg-[#1E3A2F] text-[#F7F4EB] rounded-br-md" : "bg-white border border-[#E2DED0] text-[#1A1A1A] rounded-bl-md"}`}>
        {msg.body}
        <div className={`text-[10px] mt-1 ${mine ? "text-[#DDA74A]/80" : "text-[#5C5C5C]"}`}>{timeAgo(msg.created_at)}</div>
      </div>
    </div>
  );
}

export default function Inbox() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const { conversationId } = useParams();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null); // conversation summary
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const scrollRef = useRef(null);

  const refreshList = useCallback(async () => {
    try {
      const { data } = await api.get("/conversations");
      setConvs(data.items || []);
      return data.items || [];
    } catch (e) {
      toast.error("Failed to load conversations");
      return [];
    }
  }, []);

  const loadConversation = useCallback(async (id) => {
    if (!id) {
      setActive(null);
      setMessages([]);
      return;
    }
    try {
      const [{ data: conv }, { data: msgs }] = await Promise.all([
        api.get(`/conversations/${id}`),
        api.get(`/conversations/${id}/messages`),
      ]);
      setActive(conv);
      setMessages(msgs.items || []);
      // Reduce unread badge in list
      setConvs((prev) => prev.map((c) => (c.id === id ? { ...c, unread: 0 } : c)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load conversation");
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      nav("/login");
      return;
    }
    (async () => {
      const list = await refreshList();
      setInitialLoaded(true);
      const target = conversationId || list[0]?.id;
      if (target) loadConversation(target);
    })();
  }, [loading, user, nav, refreshList, loadConversation, conversationId]);

  useEffect(() => {
    if (conversationId && initialLoaded) loadConversation(conversationId);
  }, [conversationId, initialLoaded, loadConversation]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Light polling for updates
  useEffect(() => {
    if (!user) return;
    const t = setInterval(() => {
      refreshList();
      if (active?.id) {
        api.get(`/conversations/${active.id}/messages`).then((r) => setMessages(r.items || r.data.items || []));
      }
    }, 15000);
    return () => clearInterval(t);
  }, [user, refreshList, active?.id]);

  const send = async (e) => {
    e.preventDefault();
    if (!active?.id || !body.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${active.id}/messages`, { body: body.trim() });
      setMessages((m) => [...m, data]);
      setBody("");
      setConvs((prev) => prev.map((c) => (c.id === active.id ? { ...c, last_message_preview: data.body.slice(0, 180), last_message_at: data.created_at, last_sender_id: user.id } : c)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const targetHref = useMemo(() => {
    if (!active?.target_type || !active?.target_id) return null;
    return TARGET_LINK[active.target_type]?.(active.target_id) || null;
  }, [active?.target_type, active?.target_id]);

  if (loading) return <div className="max-w-6xl mx-auto p-10 text-[#5C5C5C]">Loading…</div>;
  if (!user) return null;

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-6 py-6" data-testid="inbox-page">
      <div className="flex items-center gap-3 mb-4">
        <div className="inline-flex items-center gap-2 abm-chip"><MessageSquare className="w-3 h-3" /> Inbox</div>
        <h1 className="font-display text-3xl text-[#1E3A2F]">Messages</h1>
      </div>

      <div className="grid md:grid-cols-[340px_1fr] gap-4 h-[70vh] rounded-3xl bg-white border border-[#E2DED0] overflow-hidden">
        {/* Conversation list */}
        <aside className={`border-r border-[#E2DED0] overflow-y-auto ${active?.id ? "hidden md:block" : ""}`} data-testid="inbox-list">
          {convs.length === 0 ? (
            <div className="p-6 text-sm text-[#5C5C5C] italic">
              No conversations yet. Tap "Message" on a listing, need or business to start one — nobody has to give up their email.
            </div>
          ) : (
            <div className="divide-y divide-[#E2DED0]">
              {convs.map((c) => (
                <ConversationRow key={c.id} conv={c} active={active?.id === c.id} onClick={() => nav(`/inbox/${c.id}`)} />
              ))}
            </div>
          )}
        </aside>

        {/* Thread */}
        <section className={`flex flex-col ${!active?.id ? "hidden md:flex" : ""}`} data-testid="inbox-thread">
          {!active ? (
            <div className="m-auto text-center text-[#5C5C5C] px-6">
              <MessageSquare className="w-8 h-8 mx-auto text-[#C05A3D]" />
              <div className="mt-3 font-display text-xl text-[#1E3A2F]">Pick a conversation</div>
              <p className="text-sm mt-1">Your messages stay private inside African Bridge Media.</p>
            </div>
          ) : (
            <>
              <header className="flex items-center gap-3 px-5 py-3 border-b border-[#E2DED0]">
                <button className="md:hidden" onClick={() => nav("/inbox")} aria-label="Back">
                  <ArrowLeft className="w-5 h-5 text-[#1E3A2F]" />
                </button>
                <Avatar user={active.other} />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg text-[#1E3A2F] flex items-center gap-1 truncate" data-testid="inbox-other-name">
                    {active.other?.name || "Member"}
                    {active.other?.verified && <VerifiedBadge tier={active.other?.verification_tier} size="sm" />}
                  </div>
                  {active.target_title && (
                    <div className="text-xs text-[#5C5C5C] truncate">
                      re: <span className="text-[#C05A3D]">{active.target_title}</span>
                      {targetHref && (
                        <Link to={targetHref} className="ml-1 inline-flex items-center gap-0.5 text-[#1E3A2F] hover:text-[#C05A3D]">
                          <ExternalLink className="w-3 h-3" /> open
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              </header>

              <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-[#F7F4EB]/40">
                {messages.length === 0 ? (
                  <div className="text-center text-sm text-[#5C5C5C] italic mt-8">No messages yet — send the first one.</div>
                ) : (
                  messages.map((m) => <MessageBubble key={m.id} msg={m} mine={m.sender_id === user.id} />)
                )}
              </div>

              <form onSubmit={send} className="p-4 border-t border-[#E2DED0] bg-white">
                <div className="flex items-end gap-2">
                  <Textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Type a message…"
                    className="min-h-[52px] max-h-[160px] resize-y flex-1"
                    maxLength={2000}
                    data-testid="inbox-compose-input"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send(e);
                      }
                    }}
                  />
                  <Button type="submit" disabled={sending || !body.trim()} data-testid="inbox-send-btn" className="abm-btn-primary rounded-full h-12 px-5 shrink-0">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
